import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Percent, Globe } from 'lucide-react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const empty = {
  country: '', state: '', rate_pct: 13, label: 'Sales tax', description: '', active: true,
};

const TaxesPage = () => {
  const [rules, setRules] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    try { setRules(await api.get('/admin/taxes').then((r) => r.data)); }
    catch (e) { toast.error('Failed to load tax rules'); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ ...empty }); setOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ ...r, state: r.state || '' });
    setOpen(true);
  };

  const save = async () => {
    if (!form.country) { toast.error('Country is required'); return; }
    const body = { ...form, state: form.state || null, rate_pct: Number(form.rate_pct) };
    try {
      if (editing) {
        const patch = { ...body }; delete patch.id; delete patch.created_at; delete patch.created_by;
        await api.patch(`/admin/taxes/${editing.id}`, patch);
        toast.success('Tax rule updated');
      } else {
        await api.post('/admin/taxes', body);
        toast.success('Tax rule created');
      }
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete tax rule for ${r.country}${r.state ? ' / ' + r.state : ''}?`)) return;
    try { await api.delete(`/admin/taxes/${r.id}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div data-testid="taxes-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
            <Percent className="h-6 w-6 text-emerald-600" /> Tax rules
          </h1>
          <p className="text-slate-500 mt-1 max-w-2xl">
            Configure sales tax rates per country (and optionally per state/province). Tax is applied at checkout
            based on the <span className="font-medium">vendor's</span> country/state. State-specific rules override
            country-wide ones.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="new-tax-btn">
              <Plus className="h-4 w-4 mr-1.5" />New tax rule
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? 'Edit tax rule' : 'Create tax rule'}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Country</Label>
                  <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })}
                    placeholder="Canada" className="h-10 mt-1" disabled={!!editing} data-testid="tax-country-input" />
                </div>
                <div>
                  <Label>State / Province (optional)</Label>
                  <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })}
                    placeholder="Ontario (leave blank for country-wide)" className="h-10 mt-1" disabled={!!editing} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Rate (%)</Label>
                  <Input type="number" step="0.5" value={form.rate_pct}
                    onChange={(e) => setForm({ ...form, rate_pct: e.target.value })} className="h-10 mt-1" data-testid="tax-rate-input" />
                </div>
                <div>
                  <Label>Label</Label>
                  <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
                    placeholder="HST, GST, VAT, Sales Tax…" className="h-10 mt-1" />
                </div>
              </div>
              <div>
                <Label>Description (optional)</Label>
                <Input value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Harmonized Sales Tax — Ontario, 13%" className="h-10 mt-1" />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active
              </label>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-tax-btn">
                {editing ? 'Save' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {rules.length === 0 && (
          <div className="col-span-full text-center text-slate-500 py-12 bg-white rounded-2xl border border-slate-200 border-dashed">
            No tax rules yet. Click <span className="font-medium">New tax rule</span> to add one.
          </div>
        )}
        {rules.map((r) => (
          <div key={r.id} className="rounded-2xl bg-white border border-slate-200 p-5" data-testid={`tax-card-${r.id}`}>
            <div className="flex items-center justify-between">
              <div className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                <Globe className="h-3.5 w-3.5" /> {r.country}{r.state ? ` / ${r.state}` : ' (country-wide)'}
              </div>
              {!r.active && <Badge className="bg-slate-200 text-slate-600 hover:bg-slate-200">Inactive</Badge>}
            </div>
            <div className="mt-2 text-2xl font-semibold text-slate-900">{r.rate_pct}%</div>
            <div className="text-sm text-slate-700">{r.label}</div>
            {r.description && <div className="text-xs text-slate-500 mt-1">{r.description}</div>}
            <div className="mt-3 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => openEdit(r)}><Pencil className="h-3.5 w-3.5 mr-1" />Edit</Button>
              <Button size="sm" variant="outline" onClick={() => remove(r)} className="text-rose-600 border-rose-200 hover:bg-rose-50">
                <Trash2 className="h-3.5 w-3.5 mr-1" />Delete
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TaxesPage;
